import React, {
  useState, useEffect, useMemo, useRef, FC, memo, useCallback,
} from 'react';
import { ConnectionStatus, type DirectLine } from 'botframework-directlinejs';
import { DirectLineContext, type IDirectLineContextValue } from './direct-line-context';
import { type IAdsCopilotDirectLine, type DirectLineProviderProps } from '../../types';
import { AdsCopilotDirectLine } from './ads-copilot-direct-line';
import { IDirectLineError } from '../../botTypes';
import { defaultFetchDirectLineChannelToken } from '../../hooks/use-conversation-restoration';
import { useAdsCopilotContext } from '../ads-copilot-context-provider';
import saveConversation from './chat-history-direct-line';

export const DirectLineProvider: FC<DirectLineProviderProps> = memo((props) => {
  const [directLine, setDirectLine] = useState<AdsCopilotDirectLine | undefined>();
  const {
    endpoint,
    tokenApi = '/api/token',
    authToken = '',
    userid,
    username,
    token,
    onInitDirectLine,
    onConnectionStatusChange,
    children,
    fetchToken: fetchDirectLineChannelToken = defaultFetchDirectLineChannelToken,
    sesId = 0,
    productName,
    DirectLineClass = AdsCopilotDirectLine,
    fetchOAuthToken,
    renderErrorView,
    renderCustomErrorView,
    customStream,
    resetConversation,
    isChatHistoryEnabled,
    isMarketingIntegrationEnabled = false,
    conversationTitle = '',
    existingToken = useRef<string | null>(null),
    logger,
  } = props;
  const activitySubscriber = useRef<ReturnType<DirectLine['activity$']['subscribe']> | null>(null);
  const onInitDirectLineRef = useRef<typeof onInitDirectLine>(onInitDirectLine);
  const userIdRef = useRef<string>(userid);
  const [error, setError] = useState<IDirectLineError | null>(null);

  const adsCopilotContext = useAdsCopilotContext();

  const initDirectLine = useCallback((directLineInstance: AdsCopilotDirectLine, userIdValue: string, userNameValue: string) => {
    if (onInitDirectLineRef.current) {
      onInitDirectLineRef.current(directLineInstance, userIdValue, userNameValue);
    }
    setDirectLine(directLineInstance);

    adsCopilotContext?.onDirectLineCreated(directLineInstance, userNameValue, logger);
  }, [adsCopilotContext?.onDirectLineCreated]);

  useEffect(() => {
    setDirectLine(undefined);
    setError(null);
    let dl: IAdsCopilotDirectLine | null;

    let oAuthTokenPromise: Promise<{ token?: string | null | undefined }>;
    if (token && sesId === 0) {
      // If there is directline token passed from parent, we do not need to use oauth token to fetch directline token again
      oAuthTokenPromise = Promise.resolve({ token: '' });
    } else if (fetchOAuthToken) {
      oAuthTokenPromise = fetchOAuthToken();
    } else {
      oAuthTokenPromise = Promise.resolve({ token: authToken });
    }

    if (customStream) {
      // custom stream, initialize directline without token
      logger?.trace('[Copilot][CreateDirectLine] Initializing with custom stream');
      dl = new DirectLineClass({
        isCustomStream: true,
        userId: userIdRef.current,
        username,
        token: existingToken.current || '',
      });
      dl.activity$ = customStream;
      initDirectLine(dl as AdsCopilotDirectLine, userIdRef.current, username);
    } else if (!resetConversation?.current && existingToken.current) {
      // create a directline instance with existing conversation token
      logger?.trace(`[Copilot][CreateDirectLine] Initializing with existing directline token, existingToken.current.length=${existingToken.current.length}`);
      dl = new DirectLineClass({
        token: existingToken.current,
        userId: userIdRef.current,
        username,
      });
      initDirectLine(dl as AdsCopilotDirectLine, userIdRef.current, username);
    } else {
      if (isChatHistoryEnabled && existingToken.current) {
        saveConversation(existingToken.current, conversationTitle);
      }
      oAuthTokenPromise.then(({ token: oAuthToken }) => {
        if (token && sesId === 0) {
          // use token passed from parent component only for the initial bootstrapping
          logger?.trace(`[Copilot][CreateDirectLine] Bootstrapping with directline token passed from parent, token.length=${token.length}`);
          return Promise.resolve({ token, userid });
        }
        // fetching new conversation token to initialize new conversation
        logger?.trace(`[Copilot][CreateDirectLine] Exchanging OAuth token for new directline token, oAuthToken.length=${oAuthToken?.length}`);
        return fetchDirectLineChannelToken({
          endpoint, tokenApi: tokenApi!, authToken: oAuthToken, userid, username, isMarketingIntegrationEnabled,
        });
      }).then(({ token: fetchedToken, userid: fetchedUserId }) => {
        // create a directline instance with the fetched token
        dl = new DirectLineClass({
          token: fetchedToken,
          userId: userIdRef.current,
          username,
        });
        existingToken.current = fetchedToken;
        userIdRef.current = fetchedUserId;
        if (resetConversation) {
          resetConversation.current = false;
        }
        initDirectLine(dl as AdsCopilotDirectLine, fetchedUserId, username);
      }).catch((err) => {
        logger?.trace(`[Copilot][CreateDirectLine] Error initializing DirectLine: ${err instanceof Error ? err : JSON.stringify(err)}`);
        setError({ message: 'fetchTokenError' });
      });
    }

    return () => {
      dl = null;
    };
  }, [
    authToken,
    customStream,
    DirectLineClass,
    endpoint,
    fetchDirectLineChannelToken,
    fetchOAuthToken,
    initDirectLine,
    logger,
    productName,
    resetConversation,
    sesId,
    token,
    tokenApi,
    username,
  ]);

  // listen for connection status changes
  useEffect(() => {
    activitySubscriber.current?.unsubscribe();
    if (directLine) {
      activitySubscriber.current = directLine.connectionStatus$.subscribe((status) => {
        onConnectionStatusChange?.(status);

        if (status === ConnectionStatus.ExpiredToken) {
          setError({ message: 'directLineExpiredToken' });
        } else if (status === ConnectionStatus.FailedToConnect) {
          setError({ message: 'directLineChannelError' });
        }
      });
    }

    return () => {
      activitySubscriber.current?.unsubscribe();
    };
  }, [directLine, onConnectionStatusChange]);

  const context = useMemo<IDirectLineContextValue>(() => ({
    userid: userIdRef.current,
    username,
    endpoint,
    directLine,
    productName,
    isCustomStream: directLine?.isCustomStream || false,
  }), [directLine, endpoint, productName, username]);

  if (!context.isCustomStream && renderCustomErrorView) {
    return renderCustomErrorView();
  }

  if (error && renderErrorView != null) {
    return renderErrorView(error);
  }

  if (directLine) {
    return (
      <DirectLineContext.Provider value={context}>
        {children}
      </DirectLineContext.Provider>
    );
  }
  return null;
});
